export type CapabilityTag =
  | 'PLAN & ASSESS'
  | 'BUILD & DEPLOY'
  | 'MIGRATE & TRANSFORM'
  | 'OPERATE'
  | 'GENAI';

export const allCapabilityTags: CapabilityTag[] = [
  'PLAN & ASSESS',
  'BUILD & DEPLOY',
  'MIGRATE & TRANSFORM',
  'OPERATE',
  'GENAI',
];

export const getCapabilityTagColor = (tag: string): string => {
  switch (tag) {
    case 'PLAN & ASSESS':
      return 'green';
    case 'BUILD & DEPLOY':
      return 'cyan';
    case 'MIGRATE & TRANSFORM':
      return 'blue';
    case 'OPERATE':
      return 'orange';
    case 'GENAI':
      return 'purple'; // This will trigger the ai-tag class in CapabilityCard
    default:
      return 'default';
  }
};

export interface ICapability {
  id: string;
  title: string;
  description: string;
  tags: CapabilityTag[];
  route: string;
}

export interface ICapabilitiesFilter {
  tags: CapabilityTag[];
}

export const capabilitiesData: Record<string, ICapability> = {
  aiArchitectureGeneration: {
    id: 'architecture-generation',
    title: 'Architecture Generation',
    description:
      'Generate comprehensive cloud architecture diagrams automatically using AI based on natural language prompts. Supports both Mermaid and Diagrams formats with the ability to revise and convert architectures.',
    tags: ['PLAN & ASSESS', 'GENAI'] as CapabilityTag[],
    route: '/architecture-generation',
  },
  aiTerraformModules: {
    id: 'terraform-modules',
    title: 'Terraform Module Generation',
    description:
      'Generate, validate, and customize Terraform modules with or without AI assistance. Download production-ready Infrastructure as Code templates with best practices and security standards built-in.',
    tags: ['BUILD & DEPLOY', 'GENAI'] as CapabilityTag[],
    route: '/terraform-modules',
  },
  aiCloudAssessments: {
    id: 'cloud-assessments',
    title: 'Cloud Assessments',
    description:
      'Conduct thorough cloud readiness and optimization assessments. Generate detailed reports with actionable recommendations for cloud migration, modernization, and cost optimization strategies.',
    tags: ['PLAN & ASSESS', 'GENAI'] as CapabilityTag[],
    route: '/cloud-assessments',
  },
  aiMulesoftCodeConversion: {
    id: 'mulesoft-code-conversion',
    title: 'MuleSoft Code Conversion',
    description:
      'Seamlessly convert MuleSoft applications to AWS Lambda Node.js functions with human-in-the-loop Agentic AI. Ensure compatibility and optimize performance during the migration process.',
    tags: ['MIGRATE & TRANSFORM', 'GENAI'] as CapabilityTag[],
    route: 'https://mulesoft-code-conversion-frontend-201514097239.us-east1.run.app/',
  },
  aiCodeAnalysis: {
    id: 'ai-code-analysis',
    title: 'Agentic Code Analysis',
    description:
      'Comprehensive web application for analyzing code quality, complexity, and technical debt with dual analysis modes. Upload ZIP files or provide GitHub repository URLs to get Agentic AI-powered insights and actionable recommendations for improving your codebase.',
    tags: ['PLAN & ASSESS', 'GENAI'] as CapabilityTag[],
    route: 'https://code-analysis-frontend-201514097239.us-east1.run.app/',
  },
};

// Helper function to get all capabilities as an array (for backward compatibility)
export const getAllCapabilities = (): ICapability[] => {
  return Object.values(capabilitiesData);
};

// Helper function to get a specific capability by ID
export const getCapabilityById = (id: string): ICapability | undefined => {
  return capabilitiesData[id as keyof typeof capabilitiesData];
};

// Helper function to get all capability IDs
export const getCapabilityIds = (): string[] => {
  return Object.keys(capabilitiesData);
};
